import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSObjects: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Objects</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    The most important data structure in JavaScript — from how engines store them and property descriptors, to copying, immutability, and the interview questions that hinge on all of it.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="20 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>In a ride-sharing app, every driver needs an ID, name, phone, vehicle, rating, location, and availability. Loose variables work for one driver:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const driverId = 101;\nconst driverName = "Sachin";\nconst driverRating = 4.8;\nconst driverAvailable = true;` }]} defaultLanguage="javascript" id="obj-loose" />
   <Para>But with a million drivers it&apos;s unmanageable — and a driver isn&apos;t just data. It can accept a ride, go online/offline, update location. We need a structure that stores <Mark>data + behavior</Mark> together. That&apos;s an object.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">An <Mark>object</Mark> is a collection of related data and behavior represented as key-value pairs. Each entry is a <Mark>property</Mark>; a property whose value is a function is a <Mark>method</Mark>.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const driver = { id: 101, name: "Sachin", rating: 4.8 };` }]} defaultLanguage="javascript" id="obj-definition" />
  </section>

  {/* 3. Why */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Objects Exist</SectionHeading>
   <Para>Objects model real-world entities, each with <Mark>attributes</Mark> and <Mark>actions</Mark>:</Para>
   <div className="flex flex-wrap gap-2">
    {['User', 'Product', 'Order', 'Payment', 'Vehicle', 'Invoice'].map((e) => <Pill key={e}>{e}</Pill>)}
   </div>
  </section>

  {/* 4. Object internals */}
  <section className="mb-12">
   <SectionHeading n={4}>Object Internals</SectionHeading>
   <Para>A few facts about how JavaScript actually represents objects — these explain almost every &quot;surprising&quot; behavior later:</Para>
   <div className="my-4 space-y-2">
    {[
     ['Stored on the heap', 'A variable holds a reference (pointer) to the object, not the object itself.'],
     ['Keys are strings or Symbols', 'Even obj[1] becomes obj["1"]. Symbols allow non-colliding hidden keys.'],
     ['Every object has a hidden [[Prototype]]', 'A link to another object it inherits from — the basis of the prototype chain.'],
     ['Almost everything is an object', 'Arrays, functions, dates, and regexes are all specialized objects.'],
    ].map(([t, d]) => (
     <div key={t} className="flex items-start gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
      <span className="text-amber-500 mt-0.5">▹</span>
      <div><span className="text-[14px] font-semibold text-gray-900 dark:text-white">{t}.</span> <span className="text-[14px] text-gray-600 dark:text-gray-300">{d}</span></div>
     </div>
    ))}
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\n\nObject.getPrototypeOf(user);   // the hidden [[Prototype]] (Object.prototype)\ntypeof user;                   // "object"\ntypeof [];                     // "object" — arrays are objects\ntypeof function () {};          // "function" (a callable object)` }]} defaultLanguage="javascript" id="obj-internals" />
  </section>

  {/* 5. Creating */}
  <section className="mb-12">
   <SectionHeading n={5}>Creating Objects</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// 1. Object literal (preferred)\nconst user = { name: "Sachin", age: 28 };\n\n// 2. Empty, then fill\nconst u2 = {};\nu2.name = "Sachin";\n\n// 3. Object constructor (rare)\nconst u3 = new Object();` }]} defaultLanguage="javascript" id="obj-create" />
  </section>

  {/* 6. Accessing */}
  <section className="mb-12">
   <SectionHeading n={6}>Accessing Properties</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(user.name);     // dot notation\nconsole.log(user["name"]);  // bracket notation\n\nconst key = "age";\nconsole.log(user[key]);     // dynamic key via bracket` }]} defaultLanguage="javascript" id="obj-access" />
   <Callout tone="info" title="Which to use">Dot for normal access; bracket when the key is <Mark>dynamic</Mark> or not a valid identifier.</Callout>
  </section>

  {/* 7. Updating */}
  <section className="mb-12">
   <SectionHeading n={7}>Updating Properties</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\nuser.name = "Rahul";     // update\nuser.country = "India";  // add\ndelete user.country;     // delete` }]} defaultLanguage="javascript" id="obj-update" />
  </section>

  {/* 8. Property descriptors */}
  <section className="mb-12">
   <SectionHeading n={8}>Property Descriptors</SectionHeading>
   <Para>Every property secretly carries three flags. Normal properties have all three set to <Code>true</Code>:</Para>
   <div className="my-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
    {[
     ['writable', 'Can the value be reassigned?'],
     ['enumerable', 'Does it show up in loops / Object.keys?'],
     ['configurable', 'Can it be deleted or redefined?'],
    ].map(([t, d]) => (
     <div key={t} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="font-mono text-sm font-bold text-amber-600 dark:text-amber-400">{t}</div>
      <div className="text-[12px] text-gray-600 dark:text-gray-400 mt-1">{d}</div>
     </div>
    ))}
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\n\nObject.getOwnPropertyDescriptor(user, "name");\n// { value: "Sachin", writable: true, enumerable: true, configurable: true }\n\n// Define a locked, hidden property:\nObject.defineProperty(user, "id", {\n  value: 101,\n  writable: false,     // can't reassign\n  enumerable: false,   // hidden from Object.keys / for...in\n  configurable: false, // can't delete or redefine\n});\n\nuser.id = 999;            // silently ignored\nconsole.log(user.id);     // 101\nconsole.log(Object.keys(user)); // ["name"] — "id" is hidden` }]} defaultLanguage="javascript" id="obj-descriptors" />
  </section>

  {/* 9. Getters & setters */}
  <section className="mb-12">
   <SectionHeading n={9}>Getters &amp; Setters</SectionHeading>
   <Para>Accessor properties run a function on read or write, while still looking like a normal property:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  firstName: "Sachin",\n  lastName: "Saini",\n  get fullName() {\n    return \`\${this.firstName} \${this.lastName}\`;\n  },\n  set fullName(value) {\n    [this.firstName, this.lastName] = value.split(" ");\n  },\n};\n\nconsole.log(user.fullName);   // "Sachin Saini"  (no parentheses!)\nuser.fullName = "Rahul Kumar"; // runs the setter\nconsole.log(user.firstName);  // "Rahul"` }]} defaultLanguage="javascript" id="obj-getset" />
   <Output>{`Sachin Saini\nRahul`}</Output>
  </section>

  {/* 10. Methods & this */}
  <section className="mb-12">
   <SectionHeading n={10}>Methods &amp; this</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  greet() {\n    console.log(\`Hello \${this.name}\`);\n  },\n};\n\nuser.greet();` }]} defaultLanguage="javascript" id="obj-methods" />
   <Output>Hello Sachin</Output>
  </section>

  {/* 11. State + behavior */}
  <section className="mb-12">
   <SectionHeading n={11}>State + Behavior</SectionHeading>
   <Para>Bundling <Mark>state</Mark> with <Mark>behavior</Mark> is the foundation of OOP, classes, encapsulation, and inheritance:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const bankAccount = {\n  balance: 1000,          // state\n  deposit(amount) {       // behavior\n    this.balance += amount;\n  },\n  withdraw(amount) {\n    this.balance -= amount;\n  },\n};` }]} defaultLanguage="javascript" id="obj-state" />
  </section>

  {/* 12. Nested + dynamic */}
  <section className="mb-12">
   <SectionHeading n={12}>Nested &amp; Dynamic Properties</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  address: { city: "Bangalore", state: "Karnataka" },\n};\nconsole.log(user.address.city); // "Bangalore"\n\n// Computed (dynamic) key:\nconst key = "city";\nconst obj = { [key]: "Bangalore" }; // { city: "Bangalore" }` }]} defaultLanguage="javascript" id="obj-nested" />
  </section>

  {/* 13. Iteration */}
  <section className="mb-12">
   <SectionHeading n={13}>Iteration</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin", city: "Bangalore" };\n\nfor (const key in user) {\n  console.log(key, user[key]);\n}` }]} defaultLanguage="javascript" id="obj-forin" />
   <Output>{`name Sachin\ncity Bangalore`}</Output>
   <Callout tone="warning"><Code>for...in</Code> also walks <Mark>inherited enumerable</Mark> properties. To iterate only an object&apos;s own keys, prefer <Code>Object.keys()</Code>.</Callout>
  </section>

  {/* 14. keys/values/entries */}
  <section className="mb-12">
   <SectionHeading n={14}>Object.keys / values / entries</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin", age: 28 };\n\nObject.keys(user);    // ["name", "age"]\nObject.values(user);  // ["Sachin", 28]\nObject.entries(user); // [["name","Sachin"], ["age",28]]\n\nfor (const [k, v] of Object.entries(user)) {\n  console.log(k, v);\n}` }]} defaultLanguage="javascript" id="obj-kve" />
  </section>

  {/* 15. Property existence */}
  <section className="mb-12">
   <SectionHeading n={15}>Checking Property Existence</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin" };\n\n"name" in user;               // true (own OR inherited)\nuser.hasOwnProperty("name");  // true (own only)` }]} defaultLanguage="javascript" id="obj-exists" />
  </section>

  {/* 16. References */}
  <section className="mb-12">
   <SectionHeading n={16}>Object References</SectionHeading>
   <Para>Assignment copies the <Mark>reference</Mark>, not the object — both variables point at the same memory:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user1 = { name: "Sachin" };\nconst user2 = user1;   // same reference\nuser2.name = "Rahul";\nconsole.log(user1.name); // "Rahul"` }]} defaultLanguage="javascript" id="obj-ref" />
   <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-5">
    <div className="flex items-center justify-center gap-3 sm:gap-5 flex-wrap text-[13px] font-medium">
     <span className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200">user1</span>
     <span className="text-gray-400">↘</span>
     <span className="px-4 py-2 rounded-lg border-2 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 font-mono">{`{ name: "Rahul" }`}</span>
     <span className="text-gray-400">↙</span>
     <span className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200">user2</span>
    </div>
   </div>
  </section>

  {/* 17. Shallow vs deep copy */}
  <section className="mb-12">
   <SectionHeading n={17}>Shallow vs Deep Copy</SectionHeading>
   <Para>Spread / <Code>Object.assign</Code> copy only the top level — nested objects stay shared (the &quot;shallow copy problem&quot;):</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = { name: "Sachin", address: { city: "Bangalore" } };\n\nconst copy = { ...user };       // shallow\ncopy.address.city = "Delhi";\nconsole.log(user.address.city); // "Delhi" — nested object was shared!` }]} defaultLanguage="javascript" id="obj-shallow" />
   <Para>For a fully independent clone, use <Code>structuredClone</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const deep = structuredClone(user);          // modern, preferred\nconst deep2 = JSON.parse(JSON.stringify(user)); // older, with caveats` }]} defaultLanguage="javascript" id="obj-deep" />
  </section>

  {/* 18. freeze / seal */}
  <section className="mb-12">
   <SectionHeading n={18}>Object.freeze vs Object.seal</SectionHeading>
   <div className="my-2 grid sm:grid-cols-2 gap-3">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">Object.freeze()</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">Fully immutable — no add, delete, or change.</div>
    </div>
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
     <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">Object.seal()</div>
     <div className="text-[13px] text-gray-600 dark:text-gray-400">Update existing values, but no add or delete.</div>
    </div>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `const settings = { darkMode: true };\nObject.freeze(settings);\nsettings.darkMode = false; // ignored -> still true\n\nconst user = { name: "Sachin" };\nObject.seal(user);\nuser.name = "Rahul"; // allowed\nuser.age = 28;       // ignored (can't add)` }]} defaultLanguage="javascript" id="obj-freeze" />
   <Callout tone="info"><Code>freeze</Code> and <Code>seal</Code> are <Mark>shallow</Mark> — nested objects remain mutable.</Callout>
  </section>

  {/* 19. Across languages */}
  <section className="mb-12">
   <SectionHeading n={19}>Across Languages</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `interface User {\n  id: number;\n  name: string;\n}\nconst user: User = { id: 1, name: "Sachin" };` }]} defaultLanguage="typescript" id="obj-ts" />
   <CodeBlock snippets={[{ language: 'java', code: `class User {\n  String name;\n  User(String name) { this.name = name; }\n}\nUser user = new User("Sachin");` }]} defaultLanguage="java" id="obj-java" />
   <Callout tone="info">Java objects are <Mark>class-based</Mark>; JavaScript objects are <Mark>prototype-based</Mark>.</Callout>
  </section>

  {/* 20. Real world */}
  <section className="mb-12">
   <SectionHeading n={20}>Real-World Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const order = {\n  id: 101,\n  customer: { name: "Sachin" },\n  items: ["Pizza", "Burger"],\n  totalAmount: 1200,\n  markDelivered() {\n    console.log("Order Delivered");\n  },\n};` }]} defaultLanguage="javascript" id="obj-realworld" />
  </section>

  {/* 21. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={21}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Ignoring reference behavior">Assignment shares the same object; it doesn&apos;t clone it.</Callout>
   <Callout tone="warning" title="Assuming spread is deep">Spread copies only the top level — nested objects stay shared.</Callout>
   <Callout tone="warning" title="Expecting freeze to be deep">Frozen objects can still have mutable nested objects.</Callout>
   <Callout tone="warning" title="for...in surprises">It includes inherited enumerable keys — use <Code>Object.keys</Code> for own keys.</Callout>
  </section>

  {/* 22. Real interview questions */}
  <section className="mb-12">
   <SectionHeading n={22}>Real Interview Questions</SectionHeading>
   <QA q={<>What does <Code>{`{} === {}`}</Code> return?</>}>
    <Code>false</Code>. Two object literals are different references in memory, so they&apos;re never strictly equal — even when their contents are identical.
   </QA>
   <QA q="How do you make a property read-only?">
    Use <Code>Object.defineProperty(obj, key, {`{ writable: false }`})</Code>, or <Code>Object.freeze(obj)</Code> to lock the whole object.
   </QA>
   <QA q={<>Difference between <Code>for...in</Code> and <Code>Object.keys()</Code>?</>}>
    <Code>for...in</Code> iterates own <em>and</em> inherited enumerable properties; <Code>Object.keys()</Code> returns only the object&apos;s own enumerable keys as an array.
   </QA>
   <QA q="How do you deep-clone an object?">
    <Code>structuredClone(obj)</Code> is the modern built-in. <Code>JSON.parse(JSON.stringify(obj))</Code> works for plain data but drops functions, <Code>undefined</Code>, and <Code>Date</Code>/<Code>Map</Code> fidelity.
   </QA>
   <QA q={<>What is the output? <Code>const a = {`{x:1}`}; const b = a; b.x = 9; console.log(a.x);</Code></>}>
    <Code>9</Code>. <Code>b</Code> references the same object as <Code>a</Code>, so mutating through <Code>b</Code> is visible through <Code>a</Code>.
   </QA>
   <QA q="What is a property descriptor?">
    Hidden metadata on each property: <Code>writable</Code>, <Code>enumerable</Code>, and <Code>configurable</Code> (plus <Code>value</Code> or get/set). Read it with <Code>Object.getOwnPropertyDescriptor</Code>.
   </QA>
  </section>

  {/* 23. Mental model */}
  <section className="mb-12">
   <SectionHeading n={23}>Mental Model</SectionHeading>
   <Para>Think of an object as a <Mark>digital passport</Mark> — name, age, country, photo, number all grouped into one record. Everything about a single entity lives together.</Para>
  </section>

  {/* 24 & 25 */}
  <section className="mb-4">
   <SectionHeading n={24}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Objects are collections of key-value pairs — stored by reference, governed by property descriptors — used to model real-world entities by combining data and behavior.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Objects are the most important data structure in JavaScript — almost everything eventually becomes or behaves like one. Understand references, descriptors, and copying deeply before Arrays, Classes, Prototypes, and beyond.
   </Callout>
  </section>
 </div>
);

export default JSObjects;
